<template>
	<div class="roomvisual" @mousedown.capture.stop>
    <div class="roomvisual_title">
      <div class="name">{{ room | roomName('Chambre ') }}</div>
      <div class="roomvisual_leasable">
        <div class="label">{{ room.leasable ? 'Libre' : 'Occupée' }}</div>
        <div class="color" :style="{ 'background-color': room.leasable ? '#00A23A' : '#FBBA1F' }"></div>
      </div>
      <div style="flex-grow: 1"></div>
      <button type="button" class="close" @click="close">
        <img src="/static/images/icons/close.svg" />
      </button>
    </div>
    <div class="roomvisual_container">
      <div class="roomvisual_pics">
        <div v-if="room.images && room.images.list.length" style="position: relative">
          <hooper class="roomvisual__bigpic" :wheel-control="false"
                  @slide="ev => slide = ev.currentSlide" ref="slider">
            <hooper-slide
              v-for="(img, i) of room.images.list"
              :key="i"
              :style="imgStyle(img)"
            />
            <hooper-pagination slot="hooper-addons" />
            <hooper-navigation slot="hooper-addons" />
          </hooper>
          <div v-if="!room.leasable && room.user && room.user.avatar"
               :style="userStyle(room.user)" class="roomvisual_tenant_pic" />
          <div class="roomvisual__smallpics" v-if="room.images.list.length > 1">
            <div
              v-for="(img, i) of room.images.list"
              :key="i"
              :style="imgStyle(img)"
              :class="{ 'roomvisual__smallpics--selected': slide === i }"
              @click="slideTo(i)"
            ></div>
          </div>
        </div>
        <div class="roomvisual_stats">
          <div style="margin-bottom: 16px">
            <span style="font-size: 20px; font-weight: 600; color: #000000">{{ (room.rent_month + (room.rent_charge_month || 0) + (room.rent_supp || 0)) + (room.cooloc_fees) | money }}</span>
            <span style="font-size: 12px; font-weight: 600; color: #A6A9AC">&nbsp;par mois tout compris</span>
          </div>
          <div class="stat"><div>Loyer (hors charges)*</div><div>{{ room.rent_month | money }}</div></div>
          <div class="stat"><div>{{ room.is_provision ? 'Provisions' : 'Forfait' }} charges</div><div>{{ room.rent_charge_month | money }}</div></div>
          <div class="stat" v-if="room.rent_supp"><div>Complément de loyer</div><div>{{ room.rent_supp | money }}</div></div>
          <div class="stat roomvisual__stats__small" v-if="room.rent_supp"><div>Motif : {{ room.rent_supp_reason }}</div><div></div></div>
          <div class="stat" v-if="room.cooloc_fees > 0"><div>Frais Cooloc</div><div>{{ room.cooloc_fees | money }}</div></div>
          <div class="stat"><div>Frais d’agence</div><div>{{ room.agency_costs | money }}</div></div>
          <div class="stat-separator"></div>
          <div class="stat"><div>Dépôt de garantie</div><div>{{ room.rent_security_deposit | money }}</div></div>
          <div class="stat"><div>Type de bail</div><div>{{ home.leasing_type ? 'Individuel' : 'Unique' }}</div></div>
          <div class="stat" v-if="home.leasing_template === 'mobilite'"><div></div><div>Bail mobilité</div></div>
          <div class="stat-info" style="font-size: 12px; font-weight: 600; color: #A6A9AC">*montants à confirmer au moment de la signature</div>
        </div>
      </div>
      <div style="margin-top: 16px">
        <h3>Toutes les informations</h3>
        <div class="tags" style="margin-top: 12px">
          <div v-for="tag of roomTags" :key="tag">{{ tag }}</div>
        </div>
      </div>
      <div style="width: 100%; margin-top: 16px" v-if="sharedServices.length > 0">
        <span>Compris dans les charges</span><span style="color: #A6A9AC">&nbsp;(au forfait)</span>
        <div class="tags" style="margin-top: 12px">
          <div v-for="tag of sharedServices" :key="tag"><img src="/static/images/icons/tag-check.svg" width="16" height="16" />&nbsp;{{ tag }}</div>
        </div>
      </div>
    </div>

    <div class="roomvisual_actions">
      <ui-button type="button" @click="gotoCalendar"
                 :disabled="!room.leasable"
                 v-if="hasMandate">Visiter la chambre</ui-button>
      <ui-button type="button" :reverse="true" @click="gotoWarranty" :loading="warranty.loading"
                 :disabled="!room.leasable"
                 v-if="hasMandate">Déposer un dossier</ui-button>
    </div>
	</div>
</template>

<script>
import {
  Hooper,
  Slide as HooperSlide,
  Navigation as HooperNavigation,
  Pagination as HooperPagination,
} from 'hooper'

export default {
  props: {
    room: {
      type: Object,
      required: true,
    },
    home: {
      type: Object,
      required: true,
    },
    user: Object,
    warranty: Object,
    hasMandate: Boolean,
    // Parent functions
    close: Function,
    contactOwner: Function,
    gotoCalendar: Function,
    gotoWarranty: Function,
  },
  data: () => ({
    slide: 0,
  }),
  computed: {
    roomTags() {
      const roomTags = []
      roomTags.push(`Superficie: ${this.room.surface_area}m²`)
      return roomTags.concat(this.equipments)
    },
    equipments() {
      const room = this.room
      return Object.entries({
        has_bathroom: 'Salle de bain',
        has_balcony: 'Balcon',
        cave: 'Cave',
        has_toilet: 'Toilettes',
        has_parking: 'Parking',
        has_double_glazing: 'Double vitrage',
        has_air_conditioning: 'Climatisation',
      })
        .filter(([key, value]) => !!room[key])
        .map(([key, value]) => value)
      //TODO: .concat(room.equip_others.split(/\s*,\s*/g).map(this.$options.filters.capitalize))
        .filter(val => !!val)
    },
    sharedServices() {
      const home = this.home
      return Object.entries({
        premium_tv_included: 'Abonnement TV premium',
        has_wifi: 'Abonnement internet',
        cooking_lessons_included: 'Cours de cuisine',
        sports_lesson_included: 'Cours de sport (yoga, fitness, ...)',
        gardener_included: 'Jardinier',
        housework_included: 'Ménage',
      })
        .filter(([key, value]) => !!home[key])
        .map(([key, value]) => value)
        .concat(home.charge_included_others.split(/\s*,\s*/g).map(this.$options.filters.capitalize))
        .filter(val => !!val)
    },
  },
  methods: {
    slideTo(index) {
      this.$refs.slider.slideTo(index)
    },
    imgStyle(img) {
      return {
        backgroundColor: '#' + (0x1000000 | img.color).toString(16).substring(1),
        backgroundImage: 'url(/image/' + img.id + '/0/0)',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
      }
    },
    userStyle(user) {
      if (!user.avatar) {
        return {
          backgroundImage: 'url(/static/images/icons/no-picture.svg)',
          backgroundColor: '#A7A9AC',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
        }
      }
      return {
        backgroundColor: '#' + (0x1000000 | user.avatar.color).toString(16).substring(1),
        backgroundImage: 'url(/image/' + user.avatar.id + '/0/0)',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
      }
    },
  },
  components: {
    UiButton: require('@/ui/Button').default,
    Hooper,
    HooperSlide,
    HooperNavigation,
    HooperPagination,
  },
}
</script>

<style lang="scss">
@import "../../../static/scss/variables";

@mixin mobile() {
	@media #{$phone} {
		@content;
	}
}

@mixin pic($ratio) {
	background-size: cover;
	background-position: center;
	background-repeat: no-repeat;
	cursor: pointer;

	&::after {
		content: '';
		display: block;
		padding-bottom: $ratio;
	}
}

.roomvisual {
  padding: 16px 24px;

	display: flex;
	flex-direction: column;
	max-width: 1046px;
	background-color: #FFF;
	width: auto !important;
	height: auto !important;
	color: #766E6A;
	font-size: 16px;

  @media #{$tablet} {
    max-width: 100vw;
  }
  @media #{$phone} {
    padding: 16px 8px;
    max-width: 100vw;
  }

  .roomvisual_title {
    display: flex;
    flex-direction: row;
    height: 32px;
    margin-bottom: 16px;
    gap: 16px;
    > .name {
      line-height: 32px;
      font-size: 24px;
      color: #000000;
      font-weight: 600;
    }
    .roomvisual_leasable {
      display: flex;
      justify-content: space-between;
      height: 28px;
      padding: 2px 2px 2px 10px;
      margin: 4px 0px;
      border-radius: 28px;
      border: 1px solid #ECEAEA;
      background-color: white;
      gap: 12px;
      > div.label {
        font-size: 14px;
        font-weight: 600;
        color: #444444;
        line-height: 20px;
      }
      > div.color {
        width: 24px;
        height: 24px;
        border-radius: 24px;
        background-color: #00A23A;
      }
    }
  }

  .roomvisual_container {
    @media #{$phone} {
      max-height: calc(100vh - 260px);
      overflow-y: scroll;
    }
  }

  .roomvisual_actions {
    margin-top: 24px;
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    column-gap: 24px;
  }
  .roomvisual_actions > button {
    flex-grow: 1;
  }

  .roomvisual_pics {
    display: flex;
    flex-direction: row;
    column-gap: 32px;

    @media #{$phone} {
      flex-direction: column;
      row-gap: 32px;
    }

    .roomvisual__bigpic.hooper {
      width: 504px;
      height: 335px;

      @media #{$tablet} {
        width: 60vw;
      }
      @media #{$phone} {
        width: calc(100vw - 16px);
        height: 220px;
      }

      button.hooper-prev, button.hooper-next {
        width: 21px;
        height: 21px;
        padding: 0px;
        border-radius: 21px;
        background-color: white;
        svg {
          width: 21px;
          height: 21px;
        }
        svg path:last-child {
          fill: #C0C0C0;
        }
      }
      button.hooper-prev {
        left: 16px;
      }
      button.hooper-next {
        right: 16px;
      }
    }

    .roomvisual_tenant_pic {
      position: absolute;
      top: 247px;
      right: 16px;
      width: 72px;
      height: 72px;
      border-radius: 40px;

      @media #{$tablet} {
        left: calc(60vw - 72px - 16px);
        top: calc(335px - 72px - 16px);
      }
      @media #{$phone} {
        left: calc(100vw - 16px - 72px - 16px);
        top: calc(220px - 72px - 16px);
      }
    }

    .roomvisual__smallpics {
      margin-top: 8px;
      display: flex;
      flex-direction: row;
      column-gap: 8px;
      width: 504px;
      height: 80px;
      max-height: 80px;
      overflow-x: hidden;

      @media #{$tablet} {
        width: 60vw;
      }
      @media #{$phone} {
        display: none;
      }

      > div {
        flex: 1;
        width: 120px;
        max-width: 120px;
        height: 80px;
        @include pic(95%);
      }
    }
  }

  .roomvisual_stats {
    width: 240px;
    display: flex;
    flex-direction: column;
    row-gap: 16px;
    position: relative;

    @media #{$phone}, #{$tablet} {
      width: 70vw;
    }

    .stat {
      width: 100%;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      > div.first-child {
        font-size: 14px;
        line-height: 24px;
        font-weight: 400;
      }
      > div.last-child {
        font-size: 16px;
        line-height: 24px;
        font-weight: 600;
      }
    }

    .stat-info {
      margin-top: 24px;

      @media #{$phone} {
        position: absolute;
        top: 0;
        left: calc(100% + 16px);
        width: 20vw;
        margin-top: 0;
      }
    }

    .stat-separator {
      margin: 8px 0px;
      border-top: 1px solid #ECEAEA;
    }
  }

  h3 {
    font-weight: 600;
    font-size: 20px;
    line-height: 28px;
    color: #444444;
  }

  button.close {
    background: none;
    border: none;
    margin-top: 7px;
    width: 24px;
    height: 24px;
    padding: 0;
    flex: 24px 0 0;
    > img {
      width: 24px;
      height: 24px;
    }
  }
}
</style>
